import { type ButtonHTMLAttributes, type CSSProperties, type InputHTMLAttributes, type ReactElement, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes, useEffect, useRef, useState } from 'react';
import { EFFECT_CLASS } from '@vello/tokens';
import { Checkbox } from './check-radio';
import { UiIcon, type UiIconName } from './icons';
import { dateInputLang } from './forms';
import { Skeleton, skeletonWidth, SKELETON_DELAY_MS, useDelayedFlag } from './skeleton';

/**
 * Token-only UI primitives for the Vello Admin console — the web listing standard (Claude Design "Admin Web -
 * Passageiros Interativo", D319). Every color/space/radius/type value is a `@vello/tokens` CSS variable — never a
 * literal (the `check:visual` gate enforces this); colors and states live in `theme.css` classes so hover/press
 * can animate. Every interactive control's own box is at least 48px both ways — its hit area (D387, owner D381 #6) —
 * and a control the design draws smaller (the small pill, the icon box) is drawn inside that box; `check:targets`
 * fails an inline size below 48px on an interactive element. Each screen keeps a single primary action.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'muted' | 'danger' | 'danger-solid' | 'success';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly block?: boolean;
  /** `sm` renders the design's 34px toolbar/row pill inside a 48px hit area; `lg` is the 54px call to action (`--control-cta`). */
  readonly size?: 'md' | 'sm' | 'lg';
  readonly icon?: UiIconName;
  /**
   * Shows a spinner over the label and sets `aria-busy`; the button keeps its width and stays focusable, but a click while
   * loading is ignored. Leave it `undefined` for a button that never loads (the DOM is then exactly the plain one).
   */
  readonly loading?: boolean;
}

export function Button({ variant = 'secondary', block = false, size = 'md', icon, loading, style, className, children, onClick, ...rest }: ButtonProps): ReactElement {
  const base: CSSProperties = {
    minHeight: size === 'lg' ? 'var(--control-cta)' : 'var(--tap-target-min)',
    padding: size === 'sm' ? 0 : size === 'lg' ? '0 var(--space-6)' : '0 var(--space-5)',
    borderRadius: 'var(--radius-sm)',
    fontFamily: 'var(--font-sans)',
    fontSize: size === 'lg' ? 'var(--text-btn-lg-size)' : 'var(--text-control-size)',
    fontWeight: 'var(--text-control-weight)',
    width: block ? '100%' : undefined,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--space-2)',
  };
  const cls = `vello-btn vello-btn--${variant}${size === 'sm' ? ' vello-btn--sm' : size === 'lg' ? ' vello-btn--lg' : ''}${className !== undefined ? ` ${className}` : ''}`;
  const content = (
    <>
      {icon !== undefined && <UiIcon name={icon} size={15} />}
      {children}
    </>
  );
  // `loading` given (true or false): the label sits in its own box so it can be hidden under the spinner without changing the width.
  const inner =
    loading === undefined ? (
      content
    ) : (
      <>
        <span className="vello-btn__label">{content}</span>
        {loading && <span className="vello-btn__spinner" aria-hidden="true" />}
      </>
    );
  return (
    <button
      type="button"
      className={cls}
      style={{ ...base, ...style }}
      aria-busy={loading === true ? true : undefined}
      data-loading={loading === true ? 'true' : undefined}
      onClick={loading === true ? (e) => e.preventDefault() : onClick}
      {...rest}
    >
      {size === 'sm' ? <span className="vello-btn__pill">{inner}</span> : inner}
    </button>
  );
}

/** A 36px icon control inside a 48px hit area (header/row icon buttons of the design). */
export function IconButton({ icon, label, outlined = false, tone = 'neutral', size = 16, className, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { icon: UiIconName; label: string; outlined?: boolean; tone?: 'neutral' | 'danger'; size?: number }): ReactElement {
  const cls = `vello-iconbtn${outlined ? ' vello-iconbtn--outlined' : ''}${tone === 'danger' ? ' vello-iconbtn--danger' : ''}${className !== undefined ? ` ${className}` : ''}`;
  return (
    <button type="button" className={cls} aria-label={label} title={label} {...rest}>
      <span className="vello-iconbtn__box">
        <UiIcon name={icon} size={size} />
      </span>
    </button>
  );
}

/**
 * The card surface of a screen section. `className` lets a screen hang a theme class on it (an effect-layer entrance, a
 * `.vello-row` list host) without re-declaring the card (W6-THEME-CONSOLES, D402: the fleet console and the agent
 * desktop's forks took one).
 */
export function Panel({ children, style, padded = true, className }: { children: ReactNode; style?: CSSProperties; padded?: boolean; className?: string }): ReactElement {
  return (
    <section
      className={className}
      style={{
        background: 'var(--color-surface-card)',
        border: '1px solid var(--color-border-default)',
        borderRadius: 'var(--radius-card)',
        padding: padded ? 'var(--space-6)' : 0,
        ...style,
      }}
    >
      {children}
    </section>
  );
}

/** The page head of the listing standard: headline-role title (with an optional `badge` — the status of the record — beside it), secondary subtitle, actions right-aligned to the title block (they wrap under it on a narrow screen). */
export function PageHeader({ title, subtitle, actions, badge }: { title: string; subtitle?: string; actions?: ReactNode; badge?: ReactNode }): ReactElement {
  return (
    <header className="vello-pagehead">
      <div className="vello-pagehead__text">
        {badge === undefined ? (
          <h1 className="vello-pagehead__title">{title}</h1>
        ) : (
          <div className="vello-pagehead__titlerow">
            <h1 className="vello-pagehead__title">{title}</h1>
            {badge}
          </div>
        )}
        {subtitle !== undefined && <p className="vello-pagehead__subtitle">{subtitle}</p>}
      </div>
      {actions !== undefined && <div className="vello-pagehead__actions">{actions}</div>}
    </header>
  );
}

// The fill and ink are NOT inline: `.vello-input` sets them in theme.css so :disabled and [readonly] can change them.
const CONTROL_STYLE: CSSProperties = {
  width: '100%',
  minHeight: 'var(--tap-target-min)',
  padding: '0 var(--space-3)',
  borderRadius: 'var(--radius-sm)',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-body-size)',
};

export function TextInput({ style, className, ...rest }: InputHTMLAttributes<HTMLInputElement>): ReactElement {
  // A date input writes its day in the console's language (dd/mm/aaaa in pt-BR), not the browser's UI language.
  const lang = rest.type === 'date' && rest.lang === undefined ? dateInputLang() : rest.lang;
  return <input className={`vello-input${className !== undefined ? ` ${className}` : ''}`} style={{ ...CONTROL_STYLE, ...style }} {...rest} {...(lang !== undefined ? { lang } : {})} />;
}

/** A multi-line control (template bodies, notes) on the same token surface as `TextInput`; resizes vertically only. */
export function TextArea({ style, className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>): ReactElement {
  return <textarea className={`vello-input${className !== undefined ? ` ${className}` : ''}`} style={{ ...CONTROL_STYLE, minHeight: 120, padding: 'var(--space-3)', lineHeight: 'var(--text-body-leading)', resize: 'vertical', ...style }} {...rest} />;
}

/**
 * A ≥48px selectable row control (candidate pick lists): the shared {@link Checkbox} (D854 DS-1: it replaced a bare 20px native
 * input in a 48px label, so the input itself was a 20x20 target) - the native input covers the whole 48px label, the drawn square
 * sits beside it, and the label text toggles it. Colors come from the tokens.
 */
export function CheckRow({
  id,
  checked,
  onChange,
  children,
  disabled = false,
  describedBy,
}: {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
  /** A choice that cannot be made now (D523: the reason is written next to it and named by `describedBy`). */
  disabled?: boolean;
  describedBy?: string;
}): ReactElement {
  return <Checkbox id={id} checked={checked} onChange={onChange} disabled={disabled} label={children} {...(describedBy !== undefined ? { 'aria-describedby': describedBy } : {})} />;
}

export function Select({ style, className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode }): ReactElement {
  return (
    <select className={`vello-input${className !== undefined ? ` ${className}` : ''}`} style={{ ...CONTROL_STYLE, ...style }} {...rest}>
      {children}
    </select>
  );
}

/**
 * The listing's search field: the toolbar's 48px field, search glyph, a 48px clear control when there is a query, the
 * focus ring on the wrapper (the bare input inside draws none).
 */
export function SearchInput({ id, value, onChange, onClear, placeholder, clearLabel, autoFocus, style }: { id: string; value: string; onChange: (v: string) => void; onClear: () => void; placeholder: string; clearLabel: string; autoFocus?: boolean; style?: CSSProperties }): ReactElement {
  return (
    <div className="vello-input vello-search" style={{ flex: '1 1 280px', minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, borderRadius: 'var(--radius-sm)', padding: '0 14px', background: 'var(--color-surface-card)', color: 'var(--color-text-tertiary)', fontSize: 'var(--text-label-size)', fontWeight: 400, ...style }}>
      <UiIcon name="search" size={16} />
      <input id={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoFocus={autoFocus} aria-label={placeholder} title={placeholder} />
      {value !== '' && (
        <button type="button" className="vello-search__clear" onClick={onClear} aria-label={clearLabel} title={clearLabel}>
          <UiIcon name="x" size={13} />
        </button>
      )}
    </div>
  );
}

/**
 * The design's segmented filter: a mist track with a sliding white thumb. `value` may match no option (a quick-pick row
 * whose state was set elsewhere, like the period presets beside hand-edited dates): then nothing is pressed and the thumb
 * is not drawn.
 */
export function SegmentedControl<T extends string>({ options, value, onChange, ariaLabel }: { options: readonly { id: T; label: string }[]; value: T | undefined; onChange: (id: T) => void; ariaLabel: string }): ReactElement {
  const n = Math.max(1, options.length);
  const idx = options.findIndex((o) => o.id === value);
  return (
    <div className="vello-seg" role="group" aria-label={ariaLabel} style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }}>
      {idx >= 0 && <span className="vello-seg__thumb" aria-hidden="true" style={{ left: `calc(3px + ${idx} * (100% - 6px) / ${n})`, width: `calc((100% - 6px) / ${n})` }} />}
      {options.map((o) => (
        <button key={o.id} type="button" className="vello-seg__opt" aria-pressed={o.id === value} onClick={() => o.id !== value && onChange(o.id)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * A dropdown trigger of the listing toolbar (column picker, country filter): outlined, drawn at the toolbar's 48px
 * field height, chevron rotates while open, optional mono count; `active` keeps the pressed look while a filter is on.
 */
export function MenuButton({ label, icon, count, open, onClick, active = false, chevron = true }: { label: string; icon?: UiIconName; count?: string; open: boolean; onClick: () => void; active?: boolean; chevron?: boolean }): ReactElement {
  return (
    <button type="button" className="vello-header-btn vello-header-btn--field" aria-expanded={open} data-active={active ? 'true' : undefined} onClick={onClick}>
      {icon !== undefined && <UiIcon name={icon} size={15} />}
      {label}
      {count !== undefined && <span className="tnum" style={{ fontSize: 'var(--text-mono-sm-size)', fontWeight: 'var(--text-mono-sm-weight)', color: 'var(--color-text-tertiary)' }}>{count}</span>}
      {chevron && (
        <span className="vello-header-btn__chevron">
          <UiIcon name="chevronDown" size={13} />
        </span>
      )}
    </button>
  );
}

type Tone = 'neutral' | 'success' | 'danger' | 'warning';

const TONE_STYLE: Readonly<Record<Tone, CSSProperties>> = {
  neutral: { background: 'var(--color-border-subtle)', color: 'var(--color-text-secondary)' },
  success: { background: 'var(--color-success-soft)', color: 'var(--color-success-text)' },
  danger: { background: 'var(--color-danger-soft)', color: 'var(--color-danger-text)' },
  warning: { background: 'var(--color-warning-soft)', color: 'var(--color-warning-text)' },
};

/** The status pill of the listing standard (12px/600, 4px 10px). */
export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }): ReactElement {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 10px',
        borderRadius: 'var(--radius-pill)',
        fontSize: 'var(--text-pill-size)',
        fontWeight: 'var(--text-pill-weight)',
        lineHeight: 'var(--text-pill-leading)',
        whiteSpace: 'nowrap',
        transition: 'background var(--duration-base) var(--ease-standard), color var(--duration-base) var(--ease-standard)',
        ...TONE_STYLE[tone],
      }}
    >
      {children}
    </span>
  );
}

/**
 * The initials' size per avatar size (A08, all 600): 30 → 11, 32 → 12, 36 → 13, 46 → 15, 52 → 16; any other size scales
 * at 0.36 of its box like before D547.
 */
const AVATAR_FONT_PX: Readonly<Record<number, number>> = { 30: 11, 32: 12, 36: 13, 46: 15, 52: 16 };
function avatarFontSize(size: number): number {
  return AVATAR_FONT_PX[size] ?? Math.round(size * 0.36);
}

/**
 * Initials avatar (32px in rows, larger on a record head). D806: with `photoUrl` it draws the person's photo instead, cropped to the
 * circle; the image has an empty alt (the name is always next to it or in `title`), and a link that fails - a signed link that expired,
 * a network error - falls back to the initials in the same box, so the layout never moves.
 */
export function Avatar({ initials, size = 32, variant = 'soft', title, photoUrl }: { initials: string; size?: number; variant?: 'soft' | 'inverse' | 'chip'; title?: string; photoUrl?: string | undefined }): ReactElement {
  const [failed, setFailed] = useState<string | undefined>(undefined);
  const showPhoto = photoUrl !== undefined && photoUrl !== '' && failed !== photoUrl;
  const cls = `vello-avatar${variant === 'inverse' ? ' vello-avatar--inverse' : variant === 'chip' ? ' vello-avatar--chip' : ''}${showPhoto ? ' vello-avatar--photo' : ''}`;
  return (
    <span className={cls} title={title} aria-hidden={title === undefined} style={{ width: size, height: size, fontSize: avatarFontSize(size) }}>
      {showPhoto ? <img className="vello-avatar__img" src={photoUrl} alt="" width={size} height={size} decoding="async" onError={() => setFailed(photoUrl)} /> : initials}
    </span>
  );
}

// D549 (DS-CHARTS): the KPI tile lives in its own file (`./stat-card`, with its variants); the public names are unchanged.
export { StatCard, type StatCardProps, type StatCardVariant, type StatDelta } from './stat-card';

export function EmptyState({ title, detail }: { title: string; detail?: string }): ReactElement {
  return (
    <div style={{ padding: 'var(--space-10) var(--space-6)', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
      <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 'var(--text-title-size)', fontWeight: 'var(--text-title-weight)' }}>{title}</p>
      {detail !== undefined && <p style={{ margin: 'var(--space-2) 0 0' }}>{detail}</p>}
    </div>
  );
}

/** The listing's empty result: a search-x mark, title, hint, and an optional reset action. */
export function EmptyResult({ title, detail, action }: { title: string; detail?: string; action?: ReactNode }): ReactElement {
  return (
    <div className="vello-empty">
      <span className="vello-empty__mark">
        <UiIcon name="searchX" size={20} />
      </span>
      <div style={{ fontSize: 'var(--text-empty-title-size)', fontWeight: 'var(--text-empty-title-weight)', color: 'var(--color-text-primary)' }}>{title}</div>
      {detail !== undefined && <div style={{ fontSize: 'var(--text-caption-size)' }}>{detail}</div>}
      {action !== undefined && <div style={{ marginTop: 4 }}>{action}</div>}
    </div>
  );
}

export function Notice({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }): ReactElement {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      style={{
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 'var(--radius-sm)',
        fontSize: 'var(--text-body-size)',
        border: '1px solid var(--color-border-subtle)',
        ...TONE_STYLE[tone],
      }}
    >
      {children}
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }): ReactElement {
  return <span className="vello-kbd">{children}</span>;
}

// ---- Listing table -------------------------------------------------------------------------------------

/** A sortable column header (D674): the current direction (`undefined` = not the sort column) and the toggle; `label` is the accessible name ("Sort by Date, now newest first"). */
export interface ColumnSort {
  readonly direction: 'asc' | 'desc' | undefined;
  readonly onToggle: () => void;
  readonly label: string;
}

/**
 * D854 (L7a-026): a list cell clips its text with an ellipsis; on hover a clipped cell carries its whole text as the native
 * tooltip (one rule for every list, set only while the text really is cut - a cell that fits never gets a redundant title).
 */
function revealClippedText(e: { readonly currentTarget: HTMLElement }): void {
  const el = e.currentTarget;
  const text = el.textContent?.trim() ?? '';
  if (text !== '' && el.scrollWidth > el.clientWidth) el.setAttribute('title', text);
  else el.removeAttribute('title');
}

export interface Column<T> {
  readonly key: string;
  readonly header: string;
  readonly numeric?: boolean;
  /** D674: makes the header a real button that sorts the list by this column (the listing never sorts by itself - the caller owns the order). */
  readonly sort?: ColumnSort;
  /** D674: a filter control drawn UNDER this column's header, on the same column track (a text input, a select, a date range). Any column with one adds the filter row; a column without one leaves its cell empty. */
  readonly filter?: ReactNode;
  /** The CSS grid track (`2fr`, `176px`); defaults to `minmax(0, 1fr)` (numeric → `max-content`). */
  readonly width?: string;
  /** D854: the cell wraps onto more lines instead of cutting its text with an ellipsis (a long offer or note whose end tells two rows apart). */
  readonly wrap?: boolean;
  readonly render: (row: T) => ReactNode;
}

export interface Pagination {
  readonly from: number;
  readonly to: number;
  readonly total: number;
  readonly hasPrev: boolean;
  readonly hasNext: boolean;
  readonly onPrev: () => void;
  readonly onNext: () => void;
  readonly prevLabel: string;
  readonly nextLabel: string;
  readonly rangeLabel: string;
}

export interface ListTableStateProps {
  /** The data is still loading. See the component notes. */
  loading?: boolean;
  /** Visually hidden status text announced while loading — the app's own "Loading" string. */
  loadingLabel?: string;
  /** How many skeleton rows to draw (default 6). */
  loadingRows?: number;
  /** Delay before the skeleton shows, in ms (default 300). `0` shows it at once. */
  loadingDelayMs?: number;
  /** The load failed: the message to show in the error row. */
  error?: ReactNode;
  /** Adds a retry button to the error row (needs `retryLabel`). */
  onRetry?: () => void;
  retryLabel?: string;
}

/**
 * Replay the FX-07 cascade whenever `key` changes: the stagger container flips to `out` for a frame, then back.
 * Under reduced motion the effect layer collapses it to nothing.
 */
function useStagger(key: unknown): string {
  const [out, setOut] = useState(false);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setOut(true);
    const t = window.setTimeout(() => setOut(false), 30);
    return () => window.clearTimeout(t);
  }, [key]);
  return `${EFFECT_CLASS.stagger}${out ? ` ${EFFECT_CLASS.staggerOut}` : ''}`;
}

/**
 * The listing standard: a bordered card with a sticky mono header, 56px rows that reveal their actions on
 * hover/focus (FX-09), a selected-row rail, an empty-result slot and a paging footer. Row click is optional; row
 * actions stop propagation so a click on an action never also opens the row.
 *
 * A clickable row (D387, W4C-11) is one REAL button holding the row's cells (`.vello-list__rowbtn`, laid out on the
 * row's column tracks with CSS subgrid, so its name is the row's text), not a `role="button"` container: the row keeps
 * Tab + Enter/Space activation, its own action buttons sit beside that button (never inside another button), and the
 * open record is `aria-current="true"` on it (a button may not carry `aria-selected`).
 *
 * Column alignment (D581): the table is ONE grid (`.vello-list__grid`) that owns the tracks; the header, the body and
 * every row lay their cells on it with CSS subgrid. A row is never its own grid — a content-sized track sized per row
 * moved every `1fr` column with it, so headers and values drifted apart.
 *
 * Loading and failure (V-LIB-FORMS, D508): `loading` with no rows shows skeleton rows laid on the same column tracks
 * (after `loadingDelayMs`, default 300, so a fast load never flashes them), sets `aria-busy` and a visually hidden status
 * text from `loadingLabel`; with rows already on screen (a refresh) the rows stay and only `aria-busy` is set. `error`
 * puts a real error row above the body (message + optional retry button) and suppresses the empty state, so a failed load
 * is never mistaken for "no data" (and never an em-dash).
 */
export function ListTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  selectedKey,
  actions,
  actionsWidth = 'max-content',
  countLabel,
  empty,
  pagination,
  minWidth,
  staggerKey,
  style,
  loading = false,
  loadingLabel,
  loadingRows = 6,
  loadingDelayMs = SKELETON_DELAY_MS,
  error,
  onRetry,
  retryLabel,
}: {
  columns: readonly Column<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectedKey?: string;
  actions?: (row: T) => ReactNode;
  actionsWidth?: string;
  countLabel?: string;
  empty?: ReactNode;
  pagination?: Pagination;
  minWidth?: number;
  staggerKey?: unknown;
  style?: CSSProperties;
} & ListTableStateProps): ReactElement {
  const hasActions = actions !== undefined;
  const trailingTrack = hasActions || countLabel !== undefined;
  const tracks = [...columns.map((c) => c.width ?? (c.numeric === true ? 'max-content' : 'minmax(0, 1fr)')), ...(trailingTrack ? [actionsWidth] : [])].join(' ');
  // A numeric cell sits flush against its right edge (right-aligned, no right padding) so the right-most amount lines
  // up with the row padding. Any numeric cell with another track to its right keeps a wider gap than a text cell has (a right-aligned
  // number against a left-aligned column read "1R$ 7,00" on the desk, W4C-02/D376, and the header "PRESTADORES FORMA DE ACERTO" touched, L6b-014).
  const numericGap = (index: number): boolean => index < columns.length - 1 || trailingTrack;
  const staggerCls = useStagger(staggerKey);
  const showSkeleton = loading && rows.length === 0 && error === undefined;
  const skeletonShown = useDelayedFlag(showSkeleton, loadingDelayMs);
  return (
    <div className="vello-list" style={style} aria-busy={loading || undefined}>
      <div className="vello-list__scroll">
        <div className="vello-list__grid" style={{ minWidth, gridTemplateColumns: tracks }}>
          <div className="vello-list__head">
            {columns.map((c, i) => (
              <span key={c.key} style={{ textAlign: c.numeric === true ? 'right' : 'left', paddingRight: c.numeric === true ? (numericGap(i) ? 'var(--space-6)' : 0) : 'var(--space-3)' }}>
                {c.sort === undefined ? (
                  c.header
                ) : (
                  <button type="button" className="vello-list__sort" data-sort={c.sort.direction} aria-label={c.sort.label} onClick={c.sort.onToggle}>
                    <span>{c.header}</span>
                    {c.sort.direction !== undefined && <UiIcon name={c.sort.direction === 'asc' ? 'chevronUp' : 'chevronDown'} size={12} />}
                  </button>
                )}
              </span>
            ))}
            {(hasActions || countLabel !== undefined) && <span className="tnum" style={{ textAlign: 'right' }}>{countLabel ?? ''}</span>}
          </div>
          {columns.some((c) => c.filter !== undefined) && (
            <div className="vello-list__filters">
              {columns.map((c) => (
                <span key={c.key} className="vello-list__filter">{c.filter}</span>
              ))}
              {trailingTrack && <span />}
            </div>
          )}
          {error !== undefined && (
            <div className="vello-list__error" role="alert">
              <UiIcon name="shieldAlert" size={16} />
              <span className="vello-list__error-text">{error}</span>
              {onRetry !== undefined && retryLabel !== undefined && (
                <Button size="sm" variant="secondary" onClick={onRetry}>
                  {retryLabel}
                </Button>
              )}
            </div>
          )}
          {showSkeleton ? (
            <div className="vello-list__body vello-loading" data-pending={skeletonShown ? 'false' : 'true'} aria-hidden="true">
              {Array.from({ length: Math.max(1, Math.floor(loadingRows)) }, (_, r) => (
                <div key={r} className="vello-list__row vello-list__row--skeleton">
                  {columns.map((c, i) => (
                    <span key={c.key} className="vello-list__cell" style={{ display: 'flex', justifyContent: c.numeric === true ? 'flex-end' : 'flex-start', paddingRight: c.numeric === true ? (numericGap(i) ? 'var(--space-6)' : 0) : 'var(--space-3)' }}>
                      <Skeleton width={c.numeric === true ? '48%' : skeletonWidth(r + i)} />
                    </span>
                  ))}
                  {trailingTrack && <span />}
                </div>
              ))}
            </div>
          ) : rows.length === 0 ? (
            error !== undefined ? null : <div className="vello-list__empty">{empty ?? <EmptyState title="—" />}</div>
          ) : (
            <div className={`vello-list__body ${staggerCls}`}>
              {rows.map((row) => {
                const key = rowKey(row);
                const clickable = onRowClick !== undefined;
                const selected = selectedKey !== undefined && selectedKey === key;
                const cells = columns.map((c, i) => (
                  <span
                    key={c.key}
                    className={`vello-list__cell${c.numeric === true ? ' vello-list__cell--numeric tnum' : ''}${c.wrap === true ? ' vello-list__cell--wrap' : ''}`}
                    style={c.numeric === true && numericGap(i) ? { paddingRight: 'var(--space-6)' } : undefined}
                    onMouseEnter={revealClippedText}
                  >
                    {c.render(row)}
                  </span>
                ));
                return (
                  <div key={key} className={`vello-list__row ${EFFECT_CLASS.row}${clickable ? ' vello-list__row--clickable' : ''}`} data-selected={selected ? 'true' : undefined}>
                    {clickable ? (
                      <button type="button" className="vello-list__rowbtn" style={{ gridColumn: `1 / span ${columns.length}` }} aria-current={selected ? 'true' : undefined} onClick={() => onRowClick(row)}>
                        {cells}
                      </button>
                    ) : (
                      cells
                    )}
                    {hasActions && (
                      <div className={`vello-list__actions ${EFFECT_CLASS.rowActions}`} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                        {actions(row)}
                      </div>
                    )}
                    {!hasActions && countLabel !== undefined && <span />}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {showSkeleton && loadingLabel !== undefined && (
        <span role="status" className="vello-sr-only">
          {loadingLabel}
        </span>
      )}
      {pagination !== undefined && pagination.total > 0 && (
        <div className="vello-list__foot">
          <span className="tnum">{pagination.rangeLabel}</span>
          <span style={{ flex: 1 }} />
          <Button size="sm" variant="secondary" disabled={!pagination.hasPrev} onClick={pagination.onPrev}>{pagination.prevLabel}</Button>
          <Button size="sm" variant="secondary" disabled={!pagination.hasNext} onClick={pagination.onNext}>{pagination.nextLabel}</Button>
        </div>
      )}
    </div>
  );
}

/**
 * Backwards-compatible table for the console screens that pre-date the listing standard: same props, now
 * rendered through {@link ListTable} so every listing shares the look. A trailing `act` column becomes the
 * hover-revealed actions slot.
 */
export function Table<T>({
  columns,
  rows,
  rowKey,
  empty,
  onRowClick,
  selectedKey,
  ...state
}: {
  columns: readonly Column<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  empty?: ReactNode;
  /** The whole row opens its record (a button over the cells); a trailing `act` column keeps only the actions that do something else. */
  onRowClick?: (row: T) => void;
  /** The row whose record is open (marked `aria-current`). */
  selectedKey?: string;
} & Pick<ListTableStateProps, 'loading' | 'loadingLabel' | 'loadingRows' | 'loadingDelayMs' | 'error' | 'onRetry' | 'retryLabel'>): ReactElement {
  const last = columns[columns.length - 1];
  const hasAct = last !== undefined && last.key === 'act';
  const cols = hasAct ? columns.slice(0, -1) : columns;
  return <ListTable columns={cols} rows={rows} rowKey={rowKey} empty={empty} {...state} {...(onRowClick !== undefined ? { onRowClick } : {})} {...(selectedKey !== undefined ? { selectedKey } : {})} {...(hasAct ? { actions: (row: T) => last.render(row) } : {})} />;
}
